import styled from "styled-components"

type StyledProps = {
  $showFullSummary?: boolean
  $loading?: boolean
  $showCustomerHasPaid?: boolean
}

type PriceProps = {
  $hasDiscountPrice: boolean
}

export const Container = styled.div`
  width: 100%;
  background: ${(p) => p.theme.colors.greyLighter};
  border: 0.1rem solid ${(p) => p.theme.colors.borderLight};
  border-radius: 0.6rem;
  margin: 0 0 2rem 0;
  position: relative;
`

export const Header = styled.div`
  background: ${(p) => p.theme.colors.greyLightest};
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 1rem;
  border-radius: 0.6rem 0.6rem 0 0;
  cursor: pointer;

  > svg {
    height: 2rem;
  }
`

export const StockButton = styled.div`
  display: flex;
  align-items: center;
  font-size: 1.3rem;
  color: ${(p) => p.theme.colors.blueDark};
  transition: all 0.3s ease-in-out;
  font-weight: 500;
  margin-top: 0.5rem;

  div {
    display: flex;
    align-items: center;

    svg {
      transition: transform 0.3s ease-out;
      margin-left: 0.5rem;
      height: 1.2rem !important;
    }
  }

  &:hover {
    cursor: pointer;
    text-decoration: underline;

    svg {
      transform: translateX(0.2rem);
    }
  }

  svg {
    fill: ${(p) => p.theme.colors.blueDark};
    height: 1.3rem !important;
    width: 1.3rem !important;
    margin-right: 0.5rem !important;
  }
`

export const Content = styled.div<StyledProps>`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  padding: 2rem 2rem 0 2rem;

  ${(p) => p.theme.mQ.MEDIA_MIN_LARGE} {
    grid-template-columns: ${(p) =>
      p.$showFullSummary || !p.$showCustomerHasPaid ? "2fr 1fr" : "repeat(2, 50% 25% 25%)"};
  }

  ${(p) => p.theme.mQ.MEDIA_MIN_X_LARGE} {
  }
`

export const ImageAndDetails = styled.div`
  display: flex;
  align-items: center;
  border-bottom: 0.1rem solid ${(p) => p.theme.colors.greyLight};
  padding-bottom: 1.5rem;

  ${(p) => p.theme.mQ.MEDIA_MIN_LARGE} {
    border-bottom: none;
    padding-bottom: 0;
  }

  svg {
    height: 4rem;
    margin-right: 1rem;
  }
`

export const OrderLineTotal = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-right: 2rem;
  margin-bottom: 1rem;

  b {
    font-size: 2rem;
    line-height: 1.5rem;
    display: flex;
    align-items: center;

    svg {
      fill: ${(p) => p.theme.colors.turquoiseDarker};
      height: 1.8rem;
      margin-right: 0.5rem;
    }
  }
`

export const PriceInfo = styled.div<StyledProps>`
  transition: all 0.2s;
  max-height: ${(p) => (p.$showFullSummary ? "30rem" : "2.5rem")};
  position: relative;
  overflow: hidden;

  &:after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 5rem;
    transition: all 0.3s;
    background: ${(p) =>
      !p.$showFullSummary
        ? "linear-gradient(rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 100%)"
        : "transparent"};
  }
`

export const VatWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  padding: 1rem 2rem;

  span {
    margin-right: 5rem;
  }
`

export const Footer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;

  ${(p) => p.theme.mQ.MEDIA_MIN_MEDIUM} {
    flex-direction: row;
  }
`

export const ImageContainer = styled.div<StyledProps>`
  display: flex;
  align-items: center;
  flex: initial !important;
  align-self: center;
  margin-right: 1rem;
  height: 5rem;
  width: 5rem;
  cursor: ${(p) => (p.$loading ? "progress" : "pointer")};
  border-radius: 0.6rem;
  border: 0.1rem solid ${(p) => p.theme.colors.greyLight};
`

export const IconWrapper = styled.div<StyledProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 6rem;
  width: 6rem;
`

export const ProductImage = styled.img<StyledProps>`
  width: 100%;
  display: block;
  flex-shrink: 0;
  max-height: 100%;
  object-fit: contain;
`

export const Name = styled.h3<StyledProps>`
  margin: 0;
  display: block;
  cursor: ${(p) => (p.$loading ? "progress" : "pointer")};
`

export const CampaignName = styled.h3`
  font-size: 1.4rem;
  margin: 0;
  display: block;
  padding-right: 2rem;
  line-height: 2rem;
`

export const Id = styled.span`
  color: ${(p) => p.theme.colors.greyDark};
  display: flex;
  font-size: 1.2rem;
  line-height: 2rem;

  b {
    margin-right: 0.5rem;
  }

  svg {
    height: 1.3rem;
  }
`

export const OrderLineId = styled(Id)`
  display: flex;
  flex-direction: row;
  margin-top: 1rem;

  b {
    color: ${(p) => p.theme.colors.black};
  }
`

export const ExpandedTotalWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`

export const ExpandedSubTotalWrapper = styled(ExpandedTotalWrapper)`
  font-weight: 700;
  font-size: 1.6rem;
`

export const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 0.5rem;

  span {
    &:last-of-type {
      white-space: nowrap;
    }
  }
`

export const FieldTitle = styled.span`
  ${(p) => p.theme.bold};
  line-height: 1.8rem;
`

export const TotalTitle = styled(FieldTitle)`
  font-size: 1.5rem;
  margin-left: 1rem;

  ${(p) => p.theme.mQ.MEDIA_MIN_LARGE} {
    font-size: 1.6rem;
  }
`

export const Price = styled.span`
  display: flex;
  gap: 1rem;
  font-size: 1.5rem;
`

export const BasePrice = styled.span<PriceProps>`
  text-decoration: ${(p) => (p.$hasDiscountPrice ? "line-through" : "none")};
`

export const SalePrice = styled.span`
  color: ${(p) => p.theme.colors.turquoiseDark};
`

export const ExpandIcon = styled.span<StyledProps>`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  cursor: pointer;

  span {
    padding: 0 2rem;
    border-bottom: none;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 3rem;
    width: 100%;
    transition: all 0.2s;
    font-size: 1.3rem;

    &:hover {
      border-bottom: none;
      background-color: ${(p) => p.theme.colors.greyLightest};
    }
  }

  svg {
    margin-left: 0.5rem;
    width: 1.6rem;
    height: 1.6rem;
    transition: all 0.2s;
    overflow: hidden;
    transform: rotate(${(p) => (p.$showFullSummary ? "180deg" : "0deg")});
  }
`

export const ParentId = styled.span`
  display: flex;
  align-items: flex-start;
  a {
    text-decoration: underline;
    color: ${(p) => p.theme.colors.greyDark};
  }
`

export const VariantId = styled.span`
  display: flex;
  align-items: flex-start;

  div {
    text-decoration: underline;
    cursor: pointer;
  }
`

export const FallbackId = styled(VariantId)`
  div {
    text-decoration: none;
    cursor: default;
  }
`

export const CampaignId = styled.span`
  a {
    text-decoration: underline;
    color: ${(p) => p.theme.colors.greyDark};
  }
`

export const Qty = styled.span`
  border-bottom: 0.1rem solid ${(p) => p.theme.colors.greyLight};
  padding: 1rem 0 0.8rem;
  margin-bottom: 1rem;
  display: flex;
  flex-direction: column;

  ${(p) => p.theme.mQ.MEDIA_MIN_LARGE} {
    border-bottom: none;
    padding: 0;
  }

  div {
    margin-left: 0.5rem;
  }
`

export const Refunded = styled.span`
  padding: 0.5rem 1rem;
  background: ${(p) => p.theme.colors.yellowLighter};
  border: 0.1rem solid ${(p) => p.theme.colors.yellowBorder};
  width: fit-content;
  border-radius: 0.6rem;
`

export const Delivered = styled.div`
  border-radius: 50%;
  background: ${(p) => p.theme.colors.turquoiseDarker};
  height: 4rem;
  width: 4rem;
  display: flex;
  justify-content: center;
  align-items: center;
`

export const Info = styled.div`
  border-left: 0.1rem solid ${(p) => p.theme.colors.turquoiseDarker};
  font-size: 1rem;
  padding: 0 0.8rem;
  height: 2.5rem;
`

export const Icon = styled.div`
  padding: 0 1rem;
  background: ${(p) => p.theme.colors.turquoiseDarker};
  border-radius: 0.4rem 0 0 0.4rem;
  height: 2.5rem;

  svg {
    height: 1rem;
    fill: ${(p) => p.theme.colors.white};
  }
`

export const OperationWrapper = styled.div`
  width: 100%;
  display: flex;
  padding: 2rem 1rem;
  margin: 1rem;
  justify-content: space-between;
  div {
    display: flex;
  }
`

export const Operation = styled.div`
  background: ${(p) => p.theme.colors.white};
  padding: 0 1rem;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 2rem;
  border-radius: 0.6rem;
  font-weight: 700;
`

export const DeliveredOperation = styled(Operation)`
  background: ${(p) => p.theme.colors.delivery.fill};
  color: ${(p) => p.theme.colors.delivery.stroke};
  border: 0.1rem solid ${(p) => p.theme.colors.delivery.stroke};
`

export const ReleasedOperation = styled(Operation)`
  background: ${(p) => p.theme.colors.release.fill};
  color: ${(p) => p.theme.colors.release.stroke};
  border: 0.1rem solid ${(p) => p.theme.colors.release.stroke};
`

export const RefundedOperation = styled(Operation)`
  background: ${(p) => p.theme.colors.errorBackground};
  color: ${(p) => p.theme.colors.errorText};
  border: 0.1rem solid ${(p) => p.theme.colors.errorText};
`

export const PlacedOperation = styled(Operation)`
  background: ${(p) => p.theme.colors.refund.fill};
  color: ${(p) => p.theme.colors.refund.stroke};
  border: 0.1rem solid ${(p) => p.theme.colors.refund.stroke};
`

export const CompensatedOperation = styled(Operation)`
  background: ${(p) => p.theme.colors.compensation.fill};
  color: ${(p) => p.theme.colors.compensation.stroke};
  border: 0.1rem solid ${(p) => p.theme.colors.compensation.stroke};
`
